"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import Image from "next/image";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";
const FILE_BASE = process.env.NEXT_PUBLIC_API_BASE || "http://localhost:8000";

const VISIBLE_COUNT = 6;    // berapa logo kelihatan sekaligus
const AUTO_INTERVAL = 2000; // 2 detik

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http://") || logo.startsWith("https://")) return logo;
  return `${FILE_BASE}${logo}`;
};

export default function PartnershipSlider() {
  const [partners, setPartners] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const intervalRef = useRef(null);

  const next = () => {
    setStartIndex((prev) => {
      if (!partners.length) return 0;
      return (prev + 1) % partners.length;
    });
  };

  // load data dari API /sponsorships (public)
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${API_BASE}/sponsorships`);
        if (!res.ok) {
          let detail = "Failed to load sponsorships.";
          try {
            const data = await res.json();
            if (data?.detail) detail = data.detail;
          } catch (_) {}
          throw new Error(detail);
        }

        const data = await res.json();
        setPartners(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        const msg = err.message || "Failed to load sponsorships.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // auto slide (aktif kalau ada data)
  useEffect(() => {
    if (!partners.length) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(next, AUTO_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [partners.length]);

  // ambil logo yang kelihatan (wrap-around)
  const visiblePartners = useMemo(() => {
    if (!partners.length) return [];
    const count = Math.min(VISIBLE_COUNT, partners.length);

    return Array.from({ length: count }).map((_, i) => {
      const idx = (startIndex + i) % partners.length;
      return partners[idx];
    });
  }, [partners, startIndex]);

  if (loading && !partners.length) {
    return (
      <div className="w-full py-6">
        <div className="max-w-6xl mx-auto text-center text-xs text-zinc-500">
          Loading partners...
        </div>
      </div>
    );
  }

  if (!loading && !partners.length) {
    return (
      <div className="w-full py-6">
        <div className="max-w-6xl mx-auto text-center text-xs text-zinc-400">
          {error || "No sponsorship data to display."}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-6">
      <div className="max-w-6xl mx-auto">
        <div className="overflow-hidden">
          <div className="flex justify-center gap-8 sm:gap-12 md:gap-16 transition-opacity duration-500 ease-in-out">
            {visiblePartners.map((partner) => {
              const logoUrl = getLogoUrl(partner.logo);
              if (!logoUrl) return null;

              return (
                <div
                  key={partner.id}
                  className="flex-shrink-0 flex items-center justify-center cursor-pointer"
                >
                  <div className="relative h-14 sm:h-20 md:h-24 w-32 sm:w-40 md:w-44">
                    <img
                      src={logoUrl}
                      alt={partner.name}
                      className="object-contain h-full w-full"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {error && (
          <div className="mt-2 text-center text-[11px] text-red-500">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
